import { NgStyle } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { ConfirmationService } from 'primeng/api'
import { CheckboxModule } from 'primeng/checkbox'
import { ChipModule } from 'primeng/chip'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { DialogModule } from 'primeng/dialog'
import { DividerModule } from 'primeng/divider'
import { GalleriaModule } from 'primeng/galleria'
import { ImageModule } from 'primeng/image'
import { InputNumberModule } from 'primeng/inputnumber'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { finalize } from 'rxjs/operators'

import { ButtonComponent } from 'app/_shared/components/button/button.component'
import { EmptyPageComponent } from 'app/_shared/components/empty-page/empty-page.component'
import { ProductItemComponent } from 'app/_shared/components/product-item/product-item.component'
import { CanComponentDeactivate } from 'app/_shared/guards/required-products.guard'
import { Kit, KitProduct } from 'app/_shared/interfaces/Kit'
import { KitService } from 'app/_shared/services/kit.service'
import { AppConfigService } from 'app/app-config.service'
import { ProductComponent } from 'app/pages/product/product.component'
import { CartStore } from 'app/store/cart.store'

@Component({
  selector: 'app-kit',
  imports: [
    ProgressSpinnerModule,
    EmptyPageComponent,
    ProductItemComponent,
    NgStyle,
    DialogModule,
    ButtonComponent,
    TranslatePipe,
    CheckboxModule,
    ChipModule,
    GalleriaModule,
    ImageModule,
    InputNumberModule,
    ReactiveFormsModule,
    ProductComponent,
    ConfirmDialogModule,
    DividerModule
  ],
  providers: [ConfirmationService],
  templateUrl: './kit.component.html',
  styleUrl: './kit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class KitComponent implements OnInit, CanComponentDeactivate {
  protected readonly Number = Number
  kitService = inject(KitService)
  activatedRoute = inject(ActivatedRoute)
  appConfigService = inject(AppConfigService)
  router = inject(Router)
  cartStore = inject(CartStore)

  loading = signal(true)
  error = signal(false)
  listView = signal(true)
  leavingDialog = signal(false)
  kit = signal<Kit | undefined>(undefined)
  modalProduct = signal<KitProduct | undefined>(undefined)

  cartItemsLength = computed(() =>
    this.cartStore.items().reduce((total, item) => total + item.quantity, 0)
  )
  requiredProducts = computed(() => {
    const cartItems = this.cartStore.items()
    const kit = this.kit()
    if (kit) {
      return kit.products.filter(
        product =>
          product.product_kit_product.required &&
          !cartItems.some(cartItem => cartItem.id === product.id)
      )
    }
    return []
  })
  nonRequiredProducts = computed(() =>
    this.kit()?.products.filter(product => !product.product_kit_product.required)
  )
  maxQuantForSelection = computed(() => {
    const product = this.modalProduct()
    if (product) {
      const quant = product.product_kit_product.quantity
      const inCart = this.cartItems().filter(item => item.id === product.id).length
      const available = this.remainingSlots().length
      if (quant > inCart) {
        return Math.min(quant - inCart, available)
      }
      return 0
    }
    return undefined
  })
  cartItems = computed(() => {
    // Extract products quantity
    // In our case one product with quantity 3 should be considered as 3 products
    return this.cartStore.items().flatMap((item, index) => {
      return Array(item.quantity)
        .fill(null)
        .map(() => ({
          ...item,
          index
        }))
    })
  })
  maxProductAllowed = computed(() => this.kit()?.amount)
  overLimit = computed(() => {
    const max = this.maxProductAllowed()
    return max && this.cartItemsLength() > max ? this.cartItemsLength() - max : 0
  })
  remainingSlots = computed(() => {
    const maxAllowed = this.maxProductAllowed() || 0
    const remaining = Math.max(0, maxAllowed - this.cartItemsLength())
    return Array(remaining).fill(null)
  })

  ngOnInit() {
    if (!this.cartStore.giftingToken()) {
      this.cartStore.clearCart()
    }
    const token = this.activatedRoute.snapshot.queryParamMap.get('token')
    const userToken = this.activatedRoute.snapshot.queryParamMap.get('user_token')
    const storefrontId = this.appConfigService.getAppConfig?.id
    if (token && userToken && storefrontId) {
      this.kitService
        .getKitByToken(storefrontId, token)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: kit => {
            if (kit.data.shared_code && userToken !== kit.data.shared_code_value) {
              this.error.set(true)
              return
            }
            this.cartStore.setGiftToken(token, userToken, !!kit.data.hide_prices)
            this.kit.set(kit.data)
          },
          error: () => {
            this.error.set(true)
          }
        })
    } else {
      this.loading.set(false)
      this.error.set(true)
    }
  }

  canDeactivate(): Promise<boolean> {
    if (!this.requiredProducts().length) {
      return Promise.resolve(true)
    }

    return new Promise<boolean>(() => {
      this.leavingDialog.set(true)
    })
  }

  openModal(product: KitProduct) {
    this.modalProduct.set(product)
    this.listView.set(false)
  }

  removeProduct(index: number) {
    const item = this.cartItems()[index]
    if (item && item.quantity > 1) {
      this.cartStore.updateQuantity(index, item.quantity - 1)
    } else {
      this.cartStore.removeCartItem(index)
    }
  }

  onItemAdded() {
    this.listView.set(true)
    this.modalProduct.set(undefined)
  }
}
